import { useState, useRef, useEffect, useCallback } from 'react'
import { exercisesAPI } from '@renderer/api/exercises'
import { chatAPI } from '@renderer/api/chat'
import { useLiveKitAgent, captureLabScreenshot, ThinkingState } from '@renderer/hooks/useLiveKitAgent'
import { Message, Conversation, ExerciseDetail, SessionState } from '@renderer/types/api'

interface UseLabSessionReturn {
  exercise: ExerciseDetail | null
  loading: boolean
  error: string | null
  messages: Message[]
  inputValue: string
  isTyping: boolean
  isSending: boolean
  conversationId: string | null
  conversations: Conversation[]
  conversationsLoaded: boolean
  sessionState: SessionState | null
  pendingScreenshot: string | null
  showHistorySidebar: boolean
  searchQuery: string
  showTaskCompletePopup: boolean
  isSubmittingScreenshot: boolean
  completedTaskTitle: string | null
  setInputValue: (value: string) => void
  setSearchQuery: (query: string) => void
  handleSend: () => Promise<void>
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  loadConversation: (convId: string) => Promise<void>
  startNewConversation: () => void
  deleteConversation: (convId: string, e: React.MouseEvent) => Promise<void>
  toggleHistorySidebar: () => void
  sendMessageWithStreaming: (messageText: string, screenshotDataUrl?: string | null, displayMessage?: string) => Promise<void>
  clearScreenshot: () => void
  attachImageFile: (file: File) => void
  startCapture: () => void
  dismissTaskCompletePopup: () => void
  submitTaskScreenshot: () => void
  uploadTaskScreenshot: (file: File) => void
  retryScreenshot: () => void
  lkConnected: boolean
  lkConnecting: boolean
  lkTalking: boolean
  lkAgentStatus: 'idle' | 'listening' | 'processing' | 'speaking'
  lkSessionReady: boolean
  lkConnect: (teachingMode?: 'guide' | 'assist') => Promise<void>
  lkDisconnect: () => void
  lkSendScreenshot: (dataUrl: string) => void
  lkStartTalking: () => Promise<void>
  lkStopTalking: () => Promise<void>
  /**
   * Ask the agent to jump to a specific task. Returns false if the room
   * isn't connected or the agent hasn't finished its opening utterance —
   * call sites should treat that as a silent no-op.
   */
  lkSendJumpToTask: (taskId: number) => Promise<boolean>
  lkAssistMode: 'guide' | 'assist'
  lkSetAssistMode: (mode: 'guide' | 'assist') => Promise<void>
  lkSendHelpRequest: () => Promise<void>
  lkHelpPending: boolean
  lkHelpRejection: string | null
  lkThinking: ThinkingState | null
  lkUnverifiedTaskIds: number[]
  lkExerciseComplete: boolean
  lkJumpRejection: { taskId: number; reason: string } | null
}

export const useLabSession = (exerciseId: string | undefined): UseLabSessionReturn => {
  const [exercise, setExercise] = useState<ExerciseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [conversationsLoaded, setConversationsLoaded] = useState(false)
  const [showHistorySidebar, setShowHistorySidebar] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [pendingScreenshot, setPendingScreenshot] = useState<string | null>(null)
 
  const conversationIdRef = useRef<string | null>(null)

  useEffect(() => { conversationIdRef.current = conversationId }, [conversationId])

  const lk = useLiveKitAgent({
    exerciseId,
    conversationId,
    autoConnect: false
  })

  useEffect(() => {
    if (lk.conversationId && lk.conversationId !== conversationIdRef.current) {
      setConversationId(lk.conversationId)
      conversationIdRef.current = lk.conversationId
      // Refresh so the next "Launch → Resume" sees the session that was just created
      if (exerciseId) {
        chatAPI.getConversations(exerciseId).then(setConversations).catch(() => {})
      }
    }
  }, [lk.conversationId, exerciseId])

  useEffect(() => {
    if (!lk.agentMessages.length) return
    setMessages((prev) => {
      const idxMap = new Map(prev.map((m, i) => [m.id, i]))
      const next = [...prev]
      let changed = false
      for (const m of lk.agentMessages) {
        const entry: Message = {
          id: m.id,
          content: m.text,
          sender: m.role,
          created_at: new Date(m.ts).toISOString()
        }
        const existing = idxMap.get(m.id)
        if (existing !== undefined) {
          if (next[existing].content !== m.text) {
            next[existing] = entry
            changed = true
          }
        } else {
          next.push(entry)
          idxMap.set(m.id, next.length - 1)
          changed = true
        }
      }
      return changed ? next : prev
    })
    // Only clear the typing indicator once the last agent segment is final
    const lastAgent = [...lk.agentMessages].reverse().find((m) => m.role === 'agent')
    if (!lastAgent || lastAgent.final) setIsTyping(false)
  }, [lk.agentMessages])

  // Merge chat artifacts (post_command output) into the message stream
  // as their own bubbles. They render as code cards with a copy button.
  // Dedup by artifact id so re-renders don't duplicate.
  useEffect(() => {
    if (!lk.chatArtifacts.length) return
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id))
      const additions: Message[] = []
      for (const a of lk.chatArtifacts) {
        if (seen.has(a.id)) continue
        additions.push({
          id: a.id,
          content: '',
          sender: 'agent',
          created_at: new Date(a.ts).toISOString(),
          artifact: {
            kind: a.kind,
            language: a.language,
            content: a.content,
            note: a.note
          }
        })
      }
      return additions.length ? [...prev, ...additions] : prev
    })
  }, [lk.chatArtifacts])

  useEffect(() => {
    const fetchExercise = async () => {
      if (!exerciseId) { setLoading(false); return }
      try {
        const data = await exercisesAPI.getExerciseFull(exerciseId)
        setExercise(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchExercise()
  }, [exerciseId])

  useEffect(() => {
    const fetchConversations = async () => {
      if (!exerciseId) {
        setConversationsLoaded(true)
        return
      }
      try {
        const data = await chatAPI.getConversations(exerciseId)
        setConversations(data)
      } catch (err) {
        console.error('Error fetching conversations:', err)
      } finally {
        setConversationsLoaded(true)
      }
    }
    fetchConversations()
  }, [exerciseId])

  const lkSendScreenshotRef = useRef(lk.sendScreenshot)
  const lkSendUserTextRef = useRef(lk.sendUserText)
  useEffect(() => { lkSendScreenshotRef.current = lk.sendScreenshot }, [lk.sendScreenshot])
  useEffect(() => { lkSendUserTextRef.current = lk.sendUserText }, [lk.sendUserText])

  const sendMessageWithStreaming = useCallback(async (
    messageText: string,
    screenshotDataUrl?: string | null,
    displayMessage?: string
  ) => {
    if (!messageText.trim() || !exerciseId) return

    if (messageText.toLowerCase() !== 'start') {
      setMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          content: displayMessage ?? messageText,
          sender: 'user',
          created_at: new Date().toISOString(),
          imageDataUrl: screenshotDataUrl || undefined
        }
      ])
    }

    // Await both sends so the user-attached screenshot (if any) flushes
    // before sendUserText publishes its own auto-capture and the chat text.
    // sendUserText itself awaits its internal screenshot capture before
    // sendText, so by the time it resolves the screen state is in
    // room_state.screenshot ready for on_user_turn_completed to attach.
    if (screenshotDataUrl) {
      try { await lkSendScreenshotRef.current(screenshotDataUrl) } catch { /* logged in hook */ }
    }
    try { await lkSendUserTextRef.current(messageText) } catch { /* logged in hook */ }
    setIsTyping(true)
  }, [exerciseId])

  const handleSend = async () => {
    if (!inputValue.trim() || !exerciseId || isSending) return
    const currentInput = inputValue
    setInputValue('')
    setPendingScreenshot(null)
    setIsSending(true)
    try {
      await sendMessageWithStreaming(currentInput, pendingScreenshot)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const loadConversation = async (convId: string) => {
    // Set the session ID so the agent loads context from this session.
    // Don't pre-populate the chat with old messages — the agent's "Welcome
    // back" greeting serves as the starting point and avoids confusing the
    // user with a wall of previous conversation history.
    setConversationId(convId)
    conversationIdRef.current = convId
    setMessages([])
    setSearchQuery('')
  }

  // Clears all session state and disconnects LiveKit without reconnecting.
  // Call this on "End Session" so the next Launch always gets a fresh session.
  const resetSession = useCallback(() => {
    lk.disconnect()
    setConversationId(null)
    conversationIdRef.current = null
    setMessages([])
  }, [lk.disconnect])

  const startNewConversation = () => {
    resetSession()
    setSearchQuery('')
    lk.connect()
  }

  const deleteConversation = async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await chatAPI.deleteConversation(convId)
      setConversations((prev) => prev.filter((c) => c.id !== convId))
      if (conversationIdRef.current === convId) startNewConversation()
    } catch (err) {
      console.error('Error deleting conversation:', err)
    }
  }

  const attachImageFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result
      if (typeof dataUrl === 'string') setPendingScreenshot(dataUrl)
    }
    reader.readAsDataURL(file)
  }

  const startCapture = () => {
    const shot = captureLabScreenshot()
    if (shot) setPendingScreenshot(shot)
  }

  const clearScreenshot = () => setPendingScreenshot(null)
  const toggleHistorySidebar = () => setShowHistorySidebar((prev) => !prev)

  // Derive a partial SessionState from the voice-agent task progress so
  // the existing TaskSidebar consumers (which read sessionState?.task_id
  // and sessionState?.completed_tasks via optional chaining) keep working
  // without a wider type refactor. Other SessionState fields stay
  // undefined; every read site already guards with `?.`.
  //
  // We must produce an object whenever EITHER currentTaskId is set OR we
  // have any completedTaskIds. The previous version gated only on
  // currentTaskId, which broke two real cases:
  //   1. Reconnect replay: backend fires task_completed events before
  //      task_active, so there's a window with completedTaskIds populated
  //      but no currentTaskId yet -> strikethroughs would flash off.
  //   2. Exercise has no current task selected (e.g. just-finished-last
  //      state) -> backend never sends task_active, and past
  //      strikethroughs would be permanently invisible.
  const sessionState: SessionState | null =
    (lk.currentTaskId != null || lk.completedTaskIds.length > 0)
      ? ({
          task_id: lk.currentTaskId ?? undefined,
          completed_tasks: lk.completedTaskIds,
        } as unknown as SessionState)
      : null

  return {
    exercise,
    loading,
    error,
    messages,
    inputValue,
    isTyping,
    isSending,
    conversationId,
    conversations,
    conversationsLoaded,
    sessionState,
    pendingScreenshot,
    showHistorySidebar,
    searchQuery,
    showTaskCompletePopup: false,
    isSubmittingScreenshot: false,
    completedTaskTitle: null,
    setInputValue,
    setSearchQuery,
    handleSend,
    handleKeyPress,
    loadConversation,
    startNewConversation,
    deleteConversation,
    toggleHistorySidebar,
    sendMessageWithStreaming,
    clearScreenshot,
    attachImageFile,
    startCapture,
    dismissTaskCompletePopup: () => {},
    submitTaskScreenshot: () => {},
    uploadTaskScreenshot: () => {},
    retryScreenshot: () => {},
    lkConnected: lk.connected,
    lkConnecting: lk.connecting,
    lkTalking: lk.talking,
    lkAgentStatus: lk.agentStatus,
    lkSessionReady: lk.sessionReady,
    lkConnect: lk.connect,
    lkDisconnect: resetSession,
    lkSendScreenshot: lk.sendScreenshot,
    lkStartTalking: lk.startTalking,
    lkStopTalking: lk.stopTalking,
    lkSendJumpToTask: lk.sendJumpToTask,
    lkAssistMode: lk.assistMode,
    lkSetAssistMode: lk.sendAssistMode,
    lkSendHelpRequest: lk.sendHelpRequest,
    lkHelpPending: lk.helpPending,
    lkHelpRejection: lk.helpRejection,
    lkThinking: lk.thinking,
    lkUnverifiedTaskIds: lk.unverifiedTaskIds,
    lkExerciseComplete: lk.exerciseComplete,
    lkJumpRejection: lk.jumpRejection,
  }
}
